/* Googan homepage: card slider progress/arrows + lazy background video */
(() => {
  if (!customElements.get('gs-slider')) {
    class GsSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.gs-cs__track');
        if (!this.track) return;
        this.barEl = this.querySelector('.gs-cs__bar');
        this.thumb = this.querySelector('.gs-cs__bar span');
        this.prev = this.querySelector('[data-gs-prev]');
        this.next = this.querySelector('[data-gs-next]');
        this.schedule = () => {
          if (this.raf) return;
          this.raf = requestAnimationFrame(() => {
            this.raf = 0;
            this.update();
          });
        };
        this.track.addEventListener('scroll', this.schedule, {passive: true});
        this.prev?.addEventListener('click', () => this.go(-1));
        this.next?.addEventListener('click', () => this.go(1));
        this.ro = new ResizeObserver(() => {
          this.layout();
          this.schedule();
        });
        this.ro.observe(this.track);
        this.initBar();
        this.initDrag();
        this.layout();
        this.update();
      }
      disconnectedCallback() {
        this.ro?.disconnect();
      }
      cards() {
        return [...this.track.children];
      }
      // Decide once per resize whether every card fits (then the row just spreads out, no slider).
      layout() {
        if (!this.hasAttribute('data-spread')) return;
        this.classList.remove('gs-cs--fit');
        const cards = this.cards();
        if (!cards.length) return;
        const cs = getComputedStyle(this.track);
        const room = this.track.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
        const last = cards[cards.length - 1];
        const used = last.offsetLeft + last.offsetWidth - cards[0].offsetLeft;
        this.classList.toggle('gs-cs--fit', used <= room + 2);
      }
      // Left edge (scroll position) of each card, measured from the track's padding.
      stops() {
        const pad = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
        const max = this.track.scrollWidth - this.track.clientWidth;
        return this.cards().map((c) => Math.min(max, Math.max(0, c.offsetLeft - this.track.offsetLeft - pad)));
      }
      nearest() {
        const s = this.stops();
        const x = this.track.scrollLeft;
        let best = 0;
        s.forEach((v, i) => {
          if (Math.abs(v - x) < Math.abs(s[best] - x)) best = i;
        });
        return {s, i: best};
      }
      go(dir) {
        const {s, i} = this.nearest();
        const first = this.cards()[0];
        // move by the number of whole cards on screen
        const per = first ? Math.max(1, Math.floor((this.track.clientWidth + 2) / first.getBoundingClientRect().width)) : 1;
        const target = Math.max(0, Math.min(s.length - 1, i + dir * per));
        this.track.scrollTo({left: s[target], behavior: 'smooth'});
      }
      settle() {
        if (window.matchMedia('(hover: none)').matches) return;
        const {s, i} = this.nearest();
        this.track.scrollTo({left: s[i], behavior: 'smooth'});
      }
      update() {
        const t = this.track;
        const max = t.scrollWidth - t.clientWidth;
        const fits = max <= 2 || this.classList.contains('gs-cs--fit');
        const nav = this.querySelector('.gs-cs__nav');
        if (nav && nav.hidden !== fits) nav.hidden = fits;
        if (this.thumb && this.barEl) {
          const barW = this.barEl.clientWidth;
          const share = Math.min(1, t.clientWidth / Math.max(1, t.scrollWidth));
          const thumbW = Math.max(24, barW * share);
          const pos = max > 0 ? Math.min(1, Math.max(0, t.scrollLeft / max)) : 0;
          this.thumb.style.width = `${thumbW}px`;
          this.thumb.style.transform = `translate3d(${pos * (barW - thumbW)}px,0,0)`;
        }
        if (this.prev) this.prev.disabled = t.scrollLeft <= 2;
        if (this.next) this.next.disabled = t.scrollLeft >= max - 2;
      }
      // Progress bar: click or drag it to scroll the row.
      initBar() {
        const bar = this.barEl;
        if (!bar) return;
        let grabbing = false;
        let offset = 0;
        const thumbW = () => this.thumb.getBoundingClientRect().width;
        const scrollTo = (clientX) => {
          const r = bar.getBoundingClientRect();
          const room = r.width - thumbW();
          const pos = room > 0 ? (clientX - r.left - offset) / room : 0;
          const max = this.track.scrollWidth - this.track.clientWidth;
          this.track.scrollLeft = Math.min(1, Math.max(0, pos)) * max;
        };
        bar.addEventListener('pointerdown', (e) => {
          const tr = this.thumb.getBoundingClientRect();
          offset = e.clientX >= tr.left && e.clientX <= tr.right ? e.clientX - tr.left : thumbW() / 2;
          grabbing = true;
          this.track.style.scrollSnapType = 'none';
          bar.setPointerCapture(e.pointerId);
          scrollTo(e.clientX);
          e.preventDefault();
        });
        bar.addEventListener('pointermove', (e) => grabbing && scrollTo(e.clientX));
        const end = () => {
          if (!grabbing) return;
          grabbing = false;
          this.track.style.scrollSnapType = '';
          this.settle();
        };
        bar.addEventListener('pointerup', end);
        bar.addEventListener('pointercancel', end);
      }
      // Mouse: click and drag the row itself (touch keeps the phone's native swipe).
      initDrag() {
        const t = this.track;
        let down = false;
        let moved = false;
        let startX = 0;
        let startLeft = 0;
        t.addEventListener('pointerdown', (e) => {
          if (e.pointerType !== 'mouse' || e.button !== 0 || this.classList.contains('gs-cs--fit')) return;
          down = true;
          moved = false;
          startX = e.clientX;
          startLeft = t.scrollLeft;
        });
        window.addEventListener('pointermove', (e) => {
          if (!down) return;
          const dx = e.clientX - startX;
          if (!moved && Math.abs(dx) > 5) {
            moved = true;
            t.classList.add('is-dragging');
          }
          if (moved) t.scrollLeft = startLeft - dx;
        });
        window.addEventListener('pointerup', () => {
          if (!down) return;
          down = false;
          if (moved) {
            // let the click that follows a drag be ignored, then settle on a card
            setTimeout(() => t.classList.remove('is-dragging'), 0);
            this.settle();
          }
        });
        t.addEventListener('click', (e) => {
          if (t.classList.contains('is-dragging')) e.preventDefault();
        }, true);
        t.addEventListener('dragstart', (e) => e.preventDefault());
      }
    }
    customElements.define('gs-slider', GsSlider);
  }

  if (!customElements.get('gs-video')) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const phone = window.matchMedia('(max-width: 767px)');
    class GsVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        this.toggleBtn = this.closest('.gs-vb')?.querySelector('.gs-vb__mute');
        if (!this.video) return;
        this.loopEnd = parseFloat(this.dataset.loopEnd || '0');
        this.video.muted = true;
        this.video.addEventListener('playing', () => this.video.classList.add('is-playing'));
        if (this.loopEnd > 0) {
          this.video.addEventListener('timeupdate', () => {
            if (this.video.currentTime >= this.loopEnd) this.video.currentTime = 0;
          });
        }
        this.toggleBtn?.addEventListener('click', () => {
          this.userPaused = !this.video.paused;
          this.userPaused ? this.video.pause() : this.play();
          this.toggleBtn.classList.toggle('is-paused', this.userPaused);
        });
        if (reduce.matches) {
          this.userPaused = true;
          this.toggleBtn?.classList.add('is-paused');
        }
        this.io = new IntersectionObserver((entries) => {
          for (const e of entries) {
            if (e.isIntersecting) {
              this.load();
              if (!this.userPaused) this.play();
            } else if (this.video.src) {
              this.video.pause();
            }
          }
        }, {rootMargin: '200px 0px'});
        this.io.observe(this);
        phone.addEventListener?.('change', () => {
          if (this.video.src) {
            this.video.removeAttribute('src');
            this.load();
            if (!this.userPaused) this.play();
          }
        });
      }
      disconnectedCallback() {
        this.io?.disconnect();
      }
      load() {
        if (this.video.getAttribute('src')) return;
        const src = (phone.matches && this.dataset.srcMobile) || this.dataset.src;
        const poster = (phone.matches && this.dataset.posterMobile) || this.dataset.poster;
        if (poster) this.video.poster = poster;
        if (src) this.video.src = src;
      }
      play() {
        if (!this.video.getAttribute('src')) return;
        const p = this.video.play();
        if (p && p.catch) p.catch(() => {});
      }
    }
    customElements.define('gs-video', GsVideo);
  }

  // Theme product sliders (featured collection with "Match the Googan sliders"): same bar and arrows as our card sliders.
  const ARROW_L = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 12H5M11 6l-6 6 6 6"/></svg>';
  const ARROW_R = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12h15M13 6l6 6-6 6"/></svg>';
  function matchSwiper(section) {
    const el = section.querySelector('.swiper');
    const sw = el && el.swiper;
    const bar = section.querySelector('.swiper-controls .swiper-pagination');
    if (!sw || !bar) return false;
    if (bar.dataset.gsReady) return true;
    bar.dataset.gsReady = '1';
    const prevT = section.querySelector('.swiper-button-prev .btn__text');
    const nextT = section.querySelector('.swiper-button-next .btn__text');
    if (prevT) prevT.innerHTML = ARROW_L;
    if (nextT) nextT.innerHTML = ARROW_R;
    const thumb = document.createElement('span');
    thumb.className = 'gs-fc-thumb';
    bar.appendChild(thumb);
    const draw = () => {
      const barW = bar.clientWidth;
      const share = Math.min(1, sw.size / Math.max(1, sw.virtualSize || sw.size));
      const w = Math.max(24, barW * share);
      const pos = Math.min(1, Math.max(0, sw.progress || 0));
      thumb.style.width = `${w}px`;
      thumb.style.transform = `translate3d(${pos * (barW - w)}px,0,0)`;
    };
    sw.on('progress', draw);
    sw.on('resize', draw);
    sw.on('setTransition', (s, ms) => { thumb.style.transition = `transform ${ms}ms ease`; });
    draw();
    let grabbing = false;
    const seek = (x) => {
      const r = bar.getBoundingClientRect();
      const w = thumb.getBoundingClientRect().width;
      const p = (x - r.left - w / 2) / Math.max(1, r.width - w);
      sw.setProgress(Math.min(1, Math.max(0, p)), 0);
    };
    bar.addEventListener('pointerdown', (e) => {
      grabbing = true;
      bar.setPointerCapture(e.pointerId);
      seek(e.clientX);
      e.preventDefault();
    });
    bar.addEventListener('pointermove', (e) => grabbing && seek(e.clientX));
    const end = () => {
      if (!grabbing) return;
      grabbing = false;
      sw.slideToClosest();
    };
    bar.addEventListener('pointerup', end);
    bar.addEventListener('pointercancel', end);
    return true;
  }
  // Phones: the theme's native-scroll product rows get the same arrows at the end of their swipe bar.
  function matchMobileRow(section) {
    const bar = section.querySelector('scroll-progress-bar');
    if (!bar || bar.dataset.gsReady) return;
    const target = document.getElementById(bar.getAttribute('target'));
    if (!target) return;
    bar.dataset.gsReady = '1';
    const wrap = document.createElement('div');
    wrap.className = 'gs-fc-mnav';
    bar.parentNode.insertBefore(wrap, bar);
    wrap.appendChild(bar);
    wrap.insertAdjacentHTML('beforeend', `<div class="gs-cs__btns"><button type="button" class="gs-cs__btn" data-gs-prev aria-label="Previous">${ARROW_L}</button><button type="button" class="gs-cs__btn" data-gs-next aria-label="Next">${ARROW_R}</button></div>`);
    const prev = wrap.querySelector('[data-gs-prev]');
    const next = wrap.querySelector('[data-gs-next]');
    const step = () => {
      const item = target.querySelector('.swiper-slide, .product-card');
      const w = item ? item.getBoundingClientRect().width + 12 : target.clientWidth / 2;
      return Math.max(1, Math.floor((target.clientWidth + 2) / w)) * w;
    };
    const sync = () => {
      const max = target.scrollWidth - target.clientWidth;
      prev.disabled = target.scrollLeft <= 2;
      next.disabled = target.scrollLeft >= max - 2;
    };
    prev.addEventListener('click', () => target.scrollBy({left: -step(), behavior: 'smooth'}));
    next.addEventListener('click', () => target.scrollBy({left: step(), behavior: 'smooth'}));
    target.addEventListener('scroll', sync, {passive: true});
    sync();
  }
  const initMatched = () => {
    document.querySelectorAll('.gs-fc').forEach((section) => {
      matchMobileRow(section);
      let tries = 0;
      const tick = () => {
        if (matchSwiper(section) || ++tries > 40) return;
        setTimeout(tick, 250);
      };
      tick();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initMatched);
  else initMatched();

  // Junip reviews shown as a row: wire our progress bar and arrows to Junip's list once it has loaded.
  function wireReviewRow(section) {
    const track = section.querySelector('.junip-reviews-container');
    const nav = section.querySelector('.gs-cs__nav');
    if (!track || !nav || track.dataset.gsReady) return !!track;
    track.dataset.gsReady = '1';
    const bar = nav.querySelector('.gs-cs__bar');
    const thumb = nav.querySelector('.gs-cs__bar span');
    const prev = nav.querySelector('[data-gs-prev]');
    const next = nav.querySelector('[data-gs-next]');
    let raf = 0;
    const draw = () => {
      raf = 0;
      const max = track.scrollWidth - track.clientWidth;
      nav.hidden = max <= 2;
      const barW = bar.clientWidth;
      const w = Math.max(24, barW * Math.min(1, track.clientWidth / Math.max(1, track.scrollWidth)));
      const pos = max > 0 ? Math.min(1, Math.max(0, track.scrollLeft / max)) : 0;
      thumb.style.width = `${w}px`;
      thumb.style.transform = `translate3d(${pos * (barW - w)}px,0,0)`;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max - 2;
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(draw); };
    track.addEventListener('scroll', schedule, {passive: true});
    new ResizeObserver(schedule).observe(track);
    const step = () => {
      const card = track.querySelector('.junip-review-grid-item-container, .junip-review-list-item-container');
      const w = card ? card.getBoundingClientRect().width + 16 : track.clientWidth;
      return Math.max(1, Math.floor((track.clientWidth + 2) / w)) * w;
    };
    prev.addEventListener('click', () => track.scrollBy({left: -step(), behavior: 'smooth'}));
    next.addEventListener('click', () => track.scrollBy({left: step(), behavior: 'smooth'}));
    let grabbing = false;
    const seek = (x) => {
      const r = bar.getBoundingClientRect();
      const w = thumb.getBoundingClientRect().width;
      track.scrollLeft = Math.min(1, Math.max(0, (x - r.left - w / 2) / Math.max(1, r.width - w))) * (track.scrollWidth - track.clientWidth);
    };
    bar.addEventListener('pointerdown', (e) => { grabbing = true; bar.setPointerCapture(e.pointerId); seek(e.clientX); e.preventDefault(); });
    bar.addEventListener('pointermove', (e) => grabbing && seek(e.clientX));
    bar.addEventListener('pointerup', () => { grabbing = false; });
    bar.addEventListener('pointercancel', () => { grabbing = false; });
    draw();
    return true;
  }
  const initReviews = () => {
    document.querySelectorAll('.gs-apps--reviews').forEach((section) => {
      if (wireReviewRow(section)) return;
      const mo = new MutationObserver(() => { if (wireReviewRow(section)) mo.disconnect(); });
      mo.observe(section, {childList: true, subtree: true});
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initReviews);
  else initReviews();

  // Tap-to-copy discount code chip
  if (!customElements.get('gs-copy')) {
    class GsCopy extends HTMLElement {
      connectedCallback() {
        const btn = this.querySelector('button');
        const hint = this.querySelector('.gs-code__hint');
        btn?.addEventListener('click', async () => {
          const code = this.dataset.code || '';
          try {
            await navigator.clipboard.writeText(code);
          } catch (e) {
            const ta = document.createElement('textarea');
            ta.value = code;
            ta.setAttribute('readonly', '');
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand('copy'); } catch (err) {}
            ta.remove();
          }
          this.classList.add('is-copied');
          if (hint) hint.textContent = hint.dataset.done;
          clearTimeout(this.t);
          this.t = setTimeout(() => {
            this.classList.remove('is-copied');
            if (hint) hint.textContent = hint.dataset.idle;
          }, 2000);
        });
      }
    }
    customElements.define('gs-copy', GsCopy);
  }

  // Links to "#gs-buy" (subscription pages) scroll to the subscribe button
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href$="#gs-buy"]');
    if (!a) return;
    const btn = document.querySelector('.product-form__submit[name="add"]');
    if (!btn) return;
    e.preventDefault();
    btn.scrollIntoView({behavior: 'smooth', block: 'center'});
  });

  // Keep the sticky bar's subscription plan in sync with the plan picked in the main buy box (e.g. Monthly vs Quarterly)
  document.addEventListener('change', (e) => {
    const sel = e.target;
    if (!sel || sel.name !== 'selling_plan' || sel.hasAttribute('data-gs-sticky-plan')) return;
    document.querySelectorAll('[data-gs-sticky-plan]').forEach((i) => { if (sel.value) i.value = sel.value; });
  });
  setInterval(() => {
    const main = document.querySelector('[name="selling_plan"]:not([data-gs-sticky-plan])');
    if (!main || !main.value) return;
    document.querySelectorAll('[data-gs-sticky-plan]').forEach((i) => { if (i.value !== main.value) i.value = main.value; });
  }, 400);

  // Subscription pages: turn the Skio plan dropdown into plan buttons + a short perks list (Skio still owns the logic)
  function enhanceSkio() {
    if (!document.body.classList.contains('gs-sub-page')) return true;
    const pickers = document.querySelectorAll('fieldset.skio-plan-picker');
    if (!pickers.length) return false;
    pickers.forEach((fs) => {
      if (fs.dataset.gsEnhanced) return;
      const select = fs.querySelector('select.skio-frequency');
      const content = fs.querySelector('.skio-group-content-inner');
      if (!select || !content) return;
      fs.dataset.gsEnhanced = '1';
      const opts = [...select.options];
      const label = (o) => {
        const t = o.text.trim();
        if (/quarter/i.test(t)) return ['Every 3 months', 'One box per quarter'];
        if (/month/i.test(t) && opts.length > 1) return ['Monthly', 'A new box every month'];
        return [t.replace(/^Delivery\s+/i, ''), ''];
      };
      const price = fs.querySelector('[skio-subscription-price]');
      if (price && !fs.querySelector('.gs-per')) {
        const per = document.createElement('span');
        per.className = 'gs-per';
        price.after(per);
        const syncPer = () => { per.textContent = /quarter/i.test(select.selectedOptions[0]?.text || '') ? ' / 3 months' : ' / month'; };
        select.addEventListener('change', syncPer);
        syncPer();
      }
      if (opts.length > 1) {
        select.classList.add('gs-hidden');
        const wrap = document.createElement('div');
        wrap.className = 'gs-plan-btns';
        wrap.setAttribute('role', 'group');
        wrap.setAttribute('aria-label', 'Delivery frequency');
        opts.forEach((o) => {
          const [a, b] = label(o);
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'gs-plan-btn';
          btn.innerHTML = `<strong>${a}</strong>${b ? `<span>${b}</span>` : ''}`;
          btn.setAttribute('aria-pressed', String(o.selected));
          btn.addEventListener('click', () => {
            select.value = o.value;
            select.dispatchEvent(new Event('change', {bubbles: true}));
          });
          wrap.appendChild(btn);
        });
        const sync = () => wrap.querySelectorAll('.gs-plan-btn').forEach((b, i) => b.setAttribute('aria-pressed', String(opts[i].selected)));
        select.addEventListener('change', sync);
        content.prepend(wrap);
      } else {
        select.classList.add('gs-hidden');
      }
      const perks = document.createElement('ul');
      perks.className = 'gs-plan-perks';
      perks.innerHTML = '<li>Skip or cancel anytime</li><li>Manage it from your account</li>';
      content.appendChild(perks);
    });
    return true;
  }
  // Skio can re-render its picker (e.g. after a size change), so keep re-applying; the dataset flag makes repeats cheap.
  if (document.body.classList.contains('gs-sub-page')) {
    enhanceSkio();
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; enhanceSkio(); });
    }).observe(document.body, {childList: true, subtree: true});
  }
})();
